import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DetailedReportEntry, ProjectMember, WorkspaceRoleString } from 'api';
import { WorkspaceRoleLabels, projectService, reportsService } from 'api';
import dayjs from 'dayjs';
import {
  DeleteMenuItem,
  ReassignMenuItem,
  FILTER_FIELD_TEXT,
  MemberCellAvatar,
  MemberPrimaryText,
  MemberRowBox,
  MemberSecondaryText,
  RolePill,
} from 'features/projects/projects.styles';
import { ArrowRight, MoreHorizontal, X } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DataTableColumn } from 'shared/components/DataTable';
import { DataTable, TableScrollBox } from 'shared/components/DataTable';
import { queryKeys } from 'shared/constants/queryKeys';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { usePermissions } from 'shared/hooks/usePermissions';
import { getDateRangeShortcut } from 'shared/utils/dateUtils';

import {
  BillableBarFill,
  BillableBarRemainder,
  BillableBarTrack,
  BillableLabel,
  BillableSplitCard,
  BillableSplitHeader,
  ClientMemberStatusBadge,
  ClientTabContentStack,
  DrawerBadgeRow,
  DrawerBody,
  DrawerCard,
  DrawerCloseButton,
  DrawerContentStack,
  DrawerEmailText,
  DrawerFooter,
  DrawerHeader,
  DrawerIdentityRow,
  DrawerSectionLabel,
  DrawerStatLabel,
  DrawerStatsGrid3,
  DrawerStatValue,
  DrawerTitle,
  DRAWER_AVATAR_SX,
  DRAWER_SUBTITLE_SX,
  FilterSelect,
  FilterToolbarCard,
  MEMBER_DRAWER_BACKDROP_SX,
  MEMBER_DRAWER_PAPER_SX,
  MEMBERS_CELL_PAD_LEFT_SX,
  MEMBERS_CELL_PAD_RIGHT_SX,
  MEMBERS_CELL_PAD_RIGHT_WIDE_SX,
  MEMBERS_ROLE_FILTER_SX,
  MEMBERS_SEARCH_ICON_SX,
  MEMBERS_SECONDARY_TEXT_BLOCK_SX,
  MEMBERS_STATUS_CELL_SX,
  MEMBERS_TABLE_CONTAINER_SX,
  MEMBERS_TOOLBAR_SX,
  MEMBERS_TOTAL_CELL_SX,
  NonBillableLabel,
  PillTab,
  PillTabCount,
  PillTabsWrapper,
  PROJECT_MEMBERS_MAPPED_SINCE_SX,
  PROJECT_MEMBERS_ROW_ACTIONS_SX,
  PROJECT_MEMBERS_TABS_WRAPPER_SX,
  ProjectRowActions,
  RecentEntriesCard,
  RecentEntryBillableTag,
  RecentEntryDateText,
  RecentEntryDuration,
  RecentEntryRow,
  RecentEntryTaskText,
  SearchFieldBox,
  SearchIconWrap,
  SearchInput,
  STATUS_MENU_ITEM_SX,
  STATUS_MENU_LIST_SX,
  STATUS_MENU_PAPER_SX,
  ViewTimesheetButton,
} from '../clients.styles';

import { formatTrackedHM } from './ClientProjectsTab';

interface Props {
  projectId: string;
  projectName: string;
}

type MemberTabKey = 'mapped' | 'active-mapped' | 'active-contributors' | 'inactive' | 'exited';

// Same "all time" range hack the rest of this feature uses for all-time stats.
const ALL_TIME_FROM = '2000-01-01';
const ROLE_OPTIONS: WorkspaceRoleString[] = ['Owner', 'Admin', 'ProjectManager', 'TeamMember'];

// Design's ProjectWorkspace splits members into 5 tabs (Mapped/Active Mapped/Active
// Contributors/Inactive/Exited) driven by a mock per-project member "status" field
// (active/inactive/exited) that has no real backend equivalent — the real model only
// has one 2-state workspaceMembershipStatus (Active/Inactive). The 'exited' tab here is
// still mapped members whose workspaceMembershipStatus is Inactive — named "exited"
// (not "inactive") since it describes someone who has left the workspace while remaining
// mapped to the project, not a contribution-based inactivity.
const TAB_LABELS: Record<MemberTabKey, string> = {
  mapped: 'Mapped Members',
  'active-mapped': 'Active Mapped',
  'active-contributors': 'Active Contributors',
  inactive: 'Inactive Members',
  exited: 'Exited Members',
};

const EMPTY_MESSAGES: Record<MemberTabKey, string> = {
  mapped: 'No members are mapped to this project.',
  'active-mapped': 'No members are currently active on this project.',
  'active-contributors': 'No one has contributed time during the selected period.',
  inactive: 'No inactive members on this project.',
  exited: 'No exited members on this project.',
};

const RECENT_ENTRIES_LIMIT = 5;

const ClientProjectMembersSection: React.FC<Props> = ({ projectId, projectName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { canAssignProjectMembers } = usePermissions();
  // Local calendar date, not UTC — dayjs() defaults to the browser's local time, and this
  // needs to match the local day boundary used everywhere else in this drawer (see the
  // This Week/This Month computation below for why that distinction matters).
  const today = dayjs().format('YYYY-MM-DD');

  const [tab, setTab] = useState<MemberTabKey>('mapped');
  const [roleFilter, setRoleFilter] = useState<'all' | WorkspaceRoleString>('all');
  const [search, setSearch] = useState('');
  const [rowMenu, setRowMenu] = useState<{
    anchorEl: HTMLElement | null;
    member: ProjectMember | null;
  }>({
    anchorEl: null,
    member: null,
  });
  const [selectedMember, setSelectedMember] = useState<ProjectMember | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: members = [], isLoading } = useQuery<ProjectMember[]>({
    queryKey: queryKeys.projects.members(projectId),
    queryFn: () => projectService.getProjectMembers(projectId).then((r) => r.data.data),
  });

  // Same reports endpoint used for the hero's "Active Contributors" KPI, grouped by
  // user — also gives us each member's real total + billable tracked seconds ("Overall"
  // in the drawer, and the table's "Total" column) in one call.
  const { data: reportGroups = [] } = useQuery({
    queryKey: queryKeys.reports.summary({
      projectId,
      groupBy: 'user',
      from: ALL_TIME_FROM,
      to: today,
    }),
    queryFn: () =>
      reportsService
        .getSummary({ projectId, groupBy: 'user', from: ALL_TIME_FROM, to: today })
        .then((r) => r.data.data.groups),
  });

  const statsByUserId = useMemo(() => {
    const map = new Map<string, { total: number; billable: number }>();
    (Array.isArray(reportGroups) ? reportGroups : []).forEach((g) => {
      if (g.key) map.set(g.key, { total: g.totalDurationSecs, billable: g.billableDurationSecs });
    });
    return map;
  }, [reportGroups]);

  const totalSecondsByUserId = useMemo(() => {
    const map = new Map<string, number>();
    statsByUserId.forEach((v, k) => map.set(k, v.total));
    return map;
  }, [statsByUserId]);

  const byTab = useMemo(() => {
    const activeMapped = members.filter(
      (m) => m.workspaceMembershipStatus !== 'Inactive' && m.projectMemberStatus !== 'Inactive',
    );
    const inactive = members.filter(
      (m) => m.workspaceMembershipStatus !== 'Inactive' && m.projectMemberStatus === 'Inactive',
    );
    const exited = members.filter((m) => m.workspaceMembershipStatus === 'Inactive');
    const activeContributors = activeMapped.filter(
      (m) => (totalSecondsByUserId.get(m.userId) ?? 0) > 0,
    );
    return {
      mapped: members,
      'active-mapped': activeMapped,
      'active-contributors': activeContributors,
      inactive,
      exited,
    } satisfies Record<MemberTabKey, ProjectMember[]>;
  }, [members, totalSecondsByUserId]);

  const tabOrder: MemberTabKey[] = [
    'mapped',
    'active-mapped',
    'active-contributors',
    'inactive',
    'exited',
  ];

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return byTab[tab].filter((m) => {
      const matchesRole = roleFilter === 'all' || m.role === roleFilter;
      const matchesSearch =
        !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
      return matchesRole && matchesSearch;
    });
  }, [byTab, tab, roleFilter, search]);

  const closeRowMenu = () => setRowMenu({ anchorEl: null, member: null });

  const openMember = (member: ProjectMember) => {
    setSelectedMember(member);
    setDrawerOpen(true);
  };

  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const removeMutation = useMutation({
    mutationFn: (userId: string) => {
      setRemovingUserId(userId);
      return projectService.removeProjectMember(projectId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.members(projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.list() });
      enqueueSnackbar('Member removed', { variant: 'success' });
      setRemovingUserId(null);
      closeRowMenu();
    },
    onError: () => {
      enqueueSnackbar('Failed to remove member', { variant: 'error' });
      setRemovingUserId(null);
      closeRowMenu();
    },
  });

  const reassignMutation = useMutation({
    mutationFn: (userId: string) => projectService.addProjectMember(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.members(projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.list() });
      enqueueSnackbar('Member reassigned', { variant: 'success' });
      closeRowMenu();
    },
    onError: () => {
      enqueueSnackbar('Failed to reassign member', { variant: 'error' });
      closeRowMenu();
    },
  });

  // ── Drawer data: This Week / This Month totals, plus a recent-entries feed —
  // all scoped to the selected member on this one project, only fetched while open.
  const thisWeek = useMemo(() => getDateRangeShortcut('thisWeek'), []);
  const thisMonth = useMemo(() => getDateRangeShortcut('thisMonth'), []);
  const recentFrom = useMemo(() => dayjs().subtract(30, 'day').format('YYYY-MM-DD'), []);

  // Single entries fetch backing both "Recent entries" and the This Week/This Month tiles.
  // The range is padded a day on each side because the backend's day-boundary filtering is
  // UTC-based, so an entry right at the edge of the requested window (e.g. logged just after
  // local midnight) could otherwise be clipped before it ever reaches the frontend.
  const statsRangeFrom = useMemo(() => {
    const earliest = dayjs(thisMonth.start).isBefore(recentFrom) ? thisMonth.start : recentFrom;
    return dayjs(earliest).subtract(1, 'day').format('YYYY-MM-DD');
  }, [thisMonth.start, recentFrom]);
  const statsRangeTo = useMemo(() => dayjs().add(1, 'day').format('YYYY-MM-DD'), []);

  const { data: memberReport } = useQuery({
    queryKey: queryKeys.reports.detailed({
      projectId,
      userIds: selectedMember?.userId,
      from: statsRangeFrom,
      to: statsRangeTo,
    }),
    queryFn: () =>
      reportsService
        .getDetailedReport({
          projectId,
          userIds: selectedMember!.userId,
          from: statsRangeFrom,
          to: statsRangeTo,
        })
        .then((r) => r.data.data),
    enabled: Boolean(selectedMember) && drawerOpen,
  });

  // All-time detailed report for the selected member — used only to derive the date
  // range of their actual contributions (first entry → last entry) for the Reports link.
  const { data: allTimeMemberReport } = useQuery({
    queryKey: queryKeys.reports.detailed({
      projectId,
      userIds: selectedMember?.userId,
      from: ALL_TIME_FROM,
      to: statsRangeTo,
    }),
    queryFn: () =>
      reportsService
        .getDetailedReport({
          projectId,
          userIds: selectedMember!.userId,
          from: ALL_TIME_FROM,
          to: statsRangeTo,
        })
        .then((r) => r.data.data),
    enabled: Boolean(selectedMember) && drawerOpen,
  });

  const memberDateRange = useMemo(() => {
    const entries = allTimeMemberReport?.days.flatMap((d) => d.entries) ?? [];
    if (!entries.length) return null;
    const dates = entries.map((e) => dayjs(e.startedAt).format('YYYY-MM-DD'));
    return { from: dates.reduce((a, b) => (a < b ? a : b)), to: dates.reduce((a, b) => (a > b ? a : b)) };
  }, [allTimeMemberReport]);

  const memberEntries = useMemo<DetailedReportEntry[]>(
    () => memberReport?.days.flatMap((d) => d.entries) ?? [],
    [memberReport],
  );

  // This Week/This Month are bucketed here by each entry's own local calendar day (dayjs()
  // parses startedAt to the browser's local time), not taken from the backend's daily
  // aggregate — that table still buckets by UTC day, so an entry logged in the early-morning
  // local hours (e.g. 3am IST) is stored under the previous UTC date and would otherwise be
  // missing from "This Week"/dropped into the wrong week/month entirely.
  const { weekSecs, monthSecs } = useMemo(() => {
    let week = 0;
    let month = 0;
    memberEntries.forEach((entry) => {
      const localDate = dayjs(entry.startedAt).format('YYYY-MM-DD');
      if (localDate >= thisWeek.start && localDate <= thisWeek.end) week += entry.durationSecs;
      if (localDate >= thisMonth.start && localDate <= thisMonth.end) month += entry.durationSecs;
    });
    return { weekSecs: week, monthSecs: month };
  }, [memberEntries, thisWeek, thisMonth]);

  const recentEntries = useMemo(
    () =>
      [...memberEntries]
        .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
        .slice(0, RECENT_ENTRIES_LIMIT),
    [memberEntries],
  );

  const selectedStats = selectedMember
    ? (statsByUserId.get(selectedMember.userId) ?? { total: 0, billable: 0 })
    : { total: 0, billable: 0 };
  const nonBillableSecs = Math.max(0, selectedStats.total - selectedStats.billable);
  const billablePct =
    selectedStats.total > 0 ? Math.round((selectedStats.billable / selectedStats.total) * 100) : 0;

  const viewFullTimesheet = () => {
    if (!selectedMember) return;
    setDrawerOpen(false);
    const dateParams = memberDateRange
      ? `&start=${memberDateRange.from}&end=${memberDateRange.to}`
      : '';
    navigate(
      `${ROUTE_PATHS.REPORTS}?tab=detailed&projectIds=${projectId}&userIds=${selectedMember.userId}${dateParams}`,
    );
  };

  const columns: DataTableColumn<ProjectMember>[] = [
    {
      id: 'name',
      label: (
        <Box component="span" sx={MEMBERS_CELL_PAD_LEFT_SX}>
          MEMBER
        </Box>
      ),
      render: (member) => {
        const isRemoving = removingUserId === member.userId;
        return (
          <MemberRowBox removing={isRemoving} sx={MEMBERS_CELL_PAD_LEFT_SX}>
            <MemberCellAvatar src={member.avatarUrl} alt={member.name}>
              {member.name.charAt(0).toUpperCase()}
            </MemberCellAvatar>
            <Box minWidth={0}>
              <MemberPrimaryText variant="body2" removing={isRemoving} noWrap>
                {member.name}
              </MemberPrimaryText>
              <MemberSecondaryText
                variant="caption"
                removing={isRemoving}
                noWrap
                sx={MEMBERS_SECONDARY_TEXT_BLOCK_SX}
              >
                {member.email}
              </MemberSecondaryText>
            </Box>
          </MemberRowBox>
        );
      },
    },
    {
      // Design's ProjectMembersTable renders Role as plain secondary text, not a
      // colored pill (unlike its Status column, which is a chip) — verified against
      // the actual `<span className="text-sm text-text-secondary">` markup.
      id: 'role',
      label: 'ROLE',
      width: 140,
      render: (member) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {WorkspaceRoleLabels[member.role]}
        </Typography>
      ),
    },
    {
      id: 'total',
      label: (
        <Box component="span" sx={MEMBERS_CELL_PAD_RIGHT_WIDE_SX}>
          TOTAL
        </Box>
      ),
      align: 'right',
      width: 120,
      render: (member) => (
        <Box component="span" sx={MEMBERS_TOTAL_CELL_SX}>
          {formatTrackedHM(totalSecondsByUserId.get(member.userId) ?? 0)}
        </Box>
      ),
    },
    {
      id: 'mappedSince',
      label: 'MAPPED SINCE',
      width: 130,
      render: (member) => (
        <Box component="span" sx={PROJECT_MEMBERS_MAPPED_SINCE_SX}>
          {dayjs(member.joinedProjectAt).format('D MMM YYYY')}
        </Box>
      ),
    },
    {
      id: 'status',
      label: canAssignProjectMembers ? (
        'STATUS'
      ) : (
        <Box component="span" sx={MEMBERS_CELL_PAD_RIGHT_SX}>
          STATUS
        </Box>
      ),
      width: 110,
      render: (member) => {
        const exited = member.workspaceMembershipStatus === 'Inactive';
        const inactive = !exited && member.projectMemberStatus === 'Inactive';
        const label = exited ? 'Exited' : inactive ? 'Inactive' : 'Active';
        return (
          <Box component="span" sx={!canAssignProjectMembers ? MEMBERS_STATUS_CELL_SX : undefined}>
            <ClientMemberStatusBadge
              active={!exited && !inactive}
              label={label}
              memberLabel={label}
              size="small"
            />
          </Box>
        );
      },
    },
    ...(canAssignProjectMembers
      ? [
          {
            id: 'actions',
            label: '',
            width: 44,
            align: 'right' as const,
            render: (member: ProjectMember) => {
              const exited = member.workspaceMembershipStatus === 'Inactive';
              if (exited) return null;
              return (
                <ProjectRowActions sx={PROJECT_MEMBERS_ROW_ACTIONS_SX}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRowMenu({ anchorEl: e.currentTarget, member });
                    }}
                  >
                    <MoreHorizontal size={14} />
                  </IconButton>
                </ProjectRowActions>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <ClientTabContentStack>
      <PillTabsWrapper sx={PROJECT_MEMBERS_TABS_WRAPPER_SX}>
        {tabOrder.map((key) => (
          <PillTab key={key} type="button" active={tab === key} onClick={() => setTab(key)}>
            {TAB_LABELS[key]}
            <PillTabCount active={tab === key}>{byTab[key].length}</PillTabCount>
          </PillTab>
        ))}
      </PillTabsWrapper>

      <FilterToolbarCard sx={MEMBERS_TOOLBAR_SX}>
        <FilterSelect
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as 'all' | WorkspaceRoleString)}
          IconComponent={KeyboardArrowDownIcon}
          sx={MEMBERS_ROLE_FILTER_SX}
          MenuProps={{
            PaperProps: { sx: STATUS_MENU_PAPER_SX },
            MenuListProps: { sx: STATUS_MENU_LIST_SX },
          }}
        >
          <MenuItem value="all" sx={STATUS_MENU_ITEM_SX}>
            All roles
          </MenuItem>
          {ROLE_OPTIONS.map((role) => (
            <MenuItem key={role} value={role} sx={STATUS_MENU_ITEM_SX}>
              {WorkspaceRoleLabels[role]}
            </MenuItem>
          ))}
        </FilterSelect>

        <SearchFieldBox>
          <SearchIconWrap>
            <SearchIcon sx={MEMBERS_SEARCH_ICON_SX} />
          </SearchIconWrap>
          <SearchInput
            placeholder="Search members"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchFieldBox>
      </FilterToolbarCard>

      <TableScrollBox>
        <DataTable<ProjectMember>
          columns={columns}
          rows={filteredMembers}
          getRowId={(member) => member.userId}
          onRowClick={(userId) => {
            const member = filteredMembers.find((m) => m.userId === userId);
            if (member) openMember(member);
          }}
          loading={isLoading}
          emptyMessage={EMPTY_MESSAGES[tab]}
          borderless
          fixedLayout
          containerSx={MEMBERS_TABLE_CONTAINER_SX}
          headerBgColor="#F3F0FF"
          headerColor={FILTER_FIELD_TEXT}
          headerFontSize="10px"
          headerHeight={36}
          cellPaddingX={6}
          rowHeight={64}
          rowHoverColor="rgba(110, 79, 247, 0.03)"
        />
      </TableScrollBox>

      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {rowMenu.member?.projectMemberStatus === 'Inactive' ? (
          <ReassignMenuItem
            onClick={() => rowMenu.member && reassignMutation.mutate(rowMenu.member.userId)}
            disabled={reassignMutation.isPending}
          >
            <ListItemText>Reassign to project</ListItemText>
          </ReassignMenuItem>
        ) : (
          <DeleteMenuItem
            onClick={() => rowMenu.member && removeMutation.mutate(rowMenu.member.userId)}
            disabled={removeMutation.isPending}
          >
            <ListItemText>Remove from project</ListItemText>
          </DeleteMenuItem>
        )}
      </Menu>

      {/* Design's project-level MemberDrawer — richer than the Client Members tab's
          drawer: This Week/This Month/Overall hours, a billable split, and a real
          recent-entries feed (via reportsService.getDetailedReport), plus a footer
          link into the Reports page pre-filtered to this member + project. */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{ backdrop: { sx: MEMBER_DRAWER_BACKDROP_SX } }}
        PaperProps={{ sx: MEMBER_DRAWER_PAPER_SX }}
      >
        <DrawerHeader>
          <Box minWidth={0}>
            <DrawerTitle noWrap>{selectedMember?.name}</DrawerTitle>
            {selectedMember && (
              <Typography variant="caption" sx={DRAWER_SUBTITLE_SX} noWrap>
                {projectName}
              </Typography>
            )}
          </Box>
          <DrawerCloseButton size="small" onClick={() => setDrawerOpen(false)}>
            <X size={16} />
          </DrawerCloseButton>
        </DrawerHeader>

        <DrawerBody>
          {selectedMember && (
            <DrawerContentStack>
              <DrawerIdentityRow>
                <MemberCellAvatar
                  src={selectedMember.avatarUrl}
                  alt={selectedMember.name}
                  sx={DRAWER_AVATAR_SX}
                >
                  {selectedMember.name.charAt(0).toUpperCase()}
                </MemberCellAvatar>
                <Box minWidth={0}>
                  <DrawerBadgeRow>
                    <RolePill
                      roleKind={selectedMember.role}
                      label={WorkspaceRoleLabels[selectedMember.role]}
                      size="small"
                    />
                    {(() => {
                      const sl =
                        selectedMember.workspaceMembershipStatus === 'Inactive'
                          ? 'Exited'
                          : selectedMember.projectMemberStatus === 'Inactive'
                            ? 'Inactive'
                            : 'Active';
                      return (
                        <ClientMemberStatusBadge
                          active={sl === 'Active'}
                          label={sl}
                          memberLabel={sl}
                          size="small"
                        />
                      );
                    })()}
                  </DrawerBadgeRow>
                  <DrawerEmailText variant="caption" noWrap>
                    {selectedMember.email}
                  </DrawerEmailText>
                </Box>
              </DrawerIdentityRow>

              <DrawerStatsGrid3>
                <DrawerCard>
                  <DrawerStatLabel variant="caption">This Week</DrawerStatLabel>
                  <DrawerStatValue variant="body2">{formatTrackedHM(weekSecs)}</DrawerStatValue>
                </DrawerCard>
                <DrawerCard>
                  <DrawerStatLabel variant="caption">This Month</DrawerStatLabel>
                  <DrawerStatValue variant="body2">{formatTrackedHM(monthSecs)}</DrawerStatValue>
                </DrawerCard>
                <DrawerCard>
                  <DrawerStatLabel variant="caption">Overall</DrawerStatLabel>
                  <DrawerStatValue variant="body2" accent>
                    {formatTrackedHM(selectedStats.total)}
                  </DrawerStatValue>
                </DrawerCard>
              </DrawerStatsGrid3>

              <BillableSplitCard>
                <BillableSplitHeader>
                  <BillableLabel>
                    Billable · {formatTrackedHM(selectedStats.billable)}
                  </BillableLabel>
                  <NonBillableLabel>
                    Non-billable · {formatTrackedHM(nonBillableSecs)}
                  </NonBillableLabel>
                </BillableSplitHeader>
                <BillableBarTrack>
                  <BillableBarFill sx={{ width: `${billablePct}%` }} />
                  <BillableBarRemainder sx={{ width: `${100 - billablePct}%` }} />
                </BillableBarTrack>
              </BillableSplitCard>

              <Box>
                <DrawerSectionLabel variant="caption">Recent entries</DrawerSectionLabel>
                {recentEntries.length > 0 ? (
                  <RecentEntriesCard>
                    {recentEntries.map((entry) => (
                      <RecentEntryRow key={entry.timeEntryId}>
                        <Box minWidth={0} flex={1}>
                          <RecentEntryTaskText variant="body2" noWrap>
                            {entry.description || 'No description'}
                          </RecentEntryTaskText>
                          <RecentEntryDateText>
                            {dayjs(entry.startedAt).format('D MMM YYYY')}
                          </RecentEntryDateText>
                        </Box>
                        <RecentEntryDuration variant="body2">
                          {formatTrackedHM(entry.durationSecs)}
                        </RecentEntryDuration>
                        <RecentEntryBillableTag billable={entry.isBillable}>
                          {entry.isBillable ? 'Billable' : 'Non-billable'}
                        </RecentEntryBillableTag>
                      </RecentEntryRow>
                    ))}
                  </RecentEntriesCard>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    No entries in the last 30 days.
                  </Typography>
                )}
              </Box>
            </DrawerContentStack>
          )}
        </DrawerBody>

        {selectedMember && (
          <DrawerFooter>
            <ViewTimesheetButton type="button" onClick={viewFullTimesheet}>
              View Full Timesheet <ArrowRight size={16} />
            </ViewTimesheetButton>
          </DrawerFooter>
        )}
      </Drawer>
    </ClientTabContentStack>
  );
};

export default ClientProjectMembersSection;
